import flatten from 'lodash/flatten'
import pickBy from 'lodash/pickBy'
import pick from 'lodash/pick'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'
import { GeocoderAddressComponent } from '@commutifi-fe/interfaces'

export type GoogleAddressType = // See google docs for more details on what each of those represents

    | 'street_number'
    | 'route'
    | 'intersection'
    | 'political'
    | 'country'
    | 'administrative_area_level_1'
    | 'administrative_area_level_2'
    | 'administrative_area_level_3'
    | 'administrative_area_level_4'
    | 'administrative_area_level_5'
    | 'administrative_area_level_6'
    | 'administrative_area_level_7'
    | 'colloquial_area'
    | 'locality'
    | 'sublocality'
    | 'neighborhood'
    | 'premise'
    | 'subpremise'
    | 'plus_code'
    | 'postal_code'
    | 'natural_feature'
    | 'airport'
    | 'park'
    | 'point_of_interest'
    | 'postal_town'

/**
 * @param addrComponents - Geocoded address components returned from Google
 * @param   addressComponents - Which components of the address are required. Defaults to postal code, street or intersection
 * @param   operation - Apply OR vs. AND on required address components
 */
export const validateGoogleAddressComponents = (
  addrComponents: GeocoderAddressComponent[],
  options: { strict?: boolean } = {}
) => {
  const { strict } = options
  if (!addrComponents || !Array.isArray(addrComponents) || addrComponents.length <= 0) {
    return false
  }

  const MINIMAL_GOOGLE_ADDRESS_TYPES: Partial<Record<GoogleAddressType, boolean>> = {
    postal_code: true,
    route: true,
    intersection: !strict,
    ...(strict && {
      street_number: true,
      country: true,
      locality: true,
      sublocality: true,
      postal_town: true
    })
  }
  // Verify the address is specific enough (has a street/route, intersection or zip code)
  const addressComponentsTypes = uniq(
    flatten((addrComponents || []).map((component) => component.types as GoogleAddressType[]))
  )
  const cityAddressTypes = ['locality', 'sublocality', 'postal_town']

  // Applies an or condition, which is why we pick only the city address google types for strict checks.
  // The and conditions will be applied after on all other components
  // When the validation is strict we want a street number, street, city (locality OR sublocality OR postal_town) AND postal code
  const orConditionOnAddressComponent = (componentType: GoogleAddressType) =>
    strict
      ? pick(MINIMAL_GOOGLE_ADDRESS_TYPES, cityAddressTypes)[componentType]
      : MINIMAL_GOOGLE_ADDRESS_TYPES[componentType]

  // OR conditions execution
  if (!addressComponentsTypes.some(orConditionOnAddressComponent)) {
    return false
  }

  // AND conditions execution
  if (
    strict &&
    !Object.keys(
      omit(
        pickBy(MINIMAL_GOOGLE_ADDRESS_TYPES, (validate) => validate),
        cityAddressTypes
      )
    ).every(
      (addressType) => addressComponentsTypes.findIndex((addressComponent) => addressComponent === addressType) >= 0
    )
  ) {
    return false
  }
  return true
}

export const validatePrintedNameLength = (fullName: string, { errorMessage }: { errorMessage: string }) => {
  // This is only used for stripe billing name right now which is why we have 24.
  // Don't hesitate to make that number different or make the validation optional on this component
  // +1 is for the space between first and last name
  if (fullName.length + 1 > 24) {
    return Promise.reject(new Error(errorMessage))
  }
  return Promise.resolve()
}
